import { Box, Grid, IconButton, Typography } from '@mui/material';
import imageBackground from 'assets/images/backgrounds/bg-start-survey.jpeg';
import CloseIcon from '@mui/icons-material/Close';
import BrandLogo from 'common/components/brand-logo';
import React, { useEffect, useState } from 'react';
import { createStyleSheet, handleError } from 'utils/helpers';
import ModalShareSurvey from '../components/modal-share-survey';
import ModalConfirm from 'common/components/modal-confirm';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'common/hook';
import { resetCaptureTimeStamp } from 'slices/previewLogicPageSurveySlice';
import {
    selectSurveyImage,
    selectWelcomeTextColor,
} from 'common/theme-selectors';
import { drawReward, getUserSurveyRewards } from 'services/reward.service';
import WheelOfName, { IWheelReward } from '../components/wheel-of-name';
import { IPreviewSurveyState } from 'types/previewCampaign';
import { toast } from 'react-toastify';

interface IStartSurveyProps {
    setSurveyStep: (step: number) => void;
}
const EndSurvey: React.FC<IStartSurveyProps> = () => {
    const [openModalShareSurvey, setOpenModalShareSurvey] = useState(false);
    const [openModalCampaignStatus, setOpenModalCampaignStatus] =
        React.useState(false);
    const campaignId = useParams<{ id: string }>().id;
    const me = useAppSelector((state) => state.previewLogicPageSurvey.me);
    const surveyState = useAppSelector(
        (state) => state.previewLogicPageSurvey.surveyState
    );
    const [rewards, setRewards] = useState<IWheelReward[]>([]);
    const [winningReward, setWinningReward] = useState<IWheelReward | null>(
        null
    );
    const [isWinning, setIsWinning] = useState<boolean | undefined>(undefined);
    const [resultMessage, setResultMessage] = useState<string>('');

    const surveyImage = useAppSelector(selectSurveyImage);
    const welcomeTextColor = useAppSelector(selectWelcomeTextColor);
    const dispatch = useAppDispatch();
    const query = new URLSearchParams(useLocation().search);
    const token = query.get('token');

    useEffect(() => {
        dispatch(resetCaptureTimeStamp());
    }, [dispatch]);
    useEffect(() => {
        const fetchSurveyReward = async () => {
            try {
                const res = await getUserSurveyRewards(token, campaignId ?? '');
                if (res.success) {
                    const rewardsData: IWheelReward[] = res.data.rewards.map(
                        (reward) => ({
                            id: reward.id,
                            name: reward.attributes.name,
                            image: reward.attributes.image,
                        })
                    );
                    // Chỉ thêm phần thưởng "Chúc bạn may mắn lần sau" nếu có ít nhất 1 phần thưởng
                    if (rewardsData.length > 0) {
                        const noReward: IWheelReward = {
                            id: 0,
                            name: 'Chúc bạn may mắn lần sau',
                            image: '',
                        };
                        setRewards([...rewardsData, noReward]);
                    } else {
                        setRewards(rewardsData);
                    }
                }
            } catch (error) {
                handleError(error);
            }
        };
        fetchSurveyReward();
    }, []);

    const handleSpin = async () => {
        try {
            const res = await drawReward(token, campaignId ?? '');
            if (res.success) {
                setIsWinning(res.data.is_winning);
                setResultMessage(res.message);
                if (res.data.is_winning) {
                    setWinningReward({
                        id: res.data.reward_draw.attributes.reward.id,
                        name: res.data.reward_draw.attributes.reward.attributes
                            .name,
                        image: res.data.reward_draw.attributes.reward.attributes
                            .image,
                    });
                } else {
                    // Nếu không trúng, set phần thưởng "Chúc bạn may mắn lần sau"
                    setWinningReward({
                        id: 0,
                        name: 'Chúc bạn may mắn lần sau',
                        image: '',
                    });
                }
            } else {
                toast.error(res.message);
            }
        } catch (error) {
            handleError(error);
        }
    };
    const navigate = useNavigate();
    const closeSurvey = (campaignId: number, me: IPreviewSurveyState['me']) => {
        let urlToBack = '';
        if (me.creator) {
            urlToBack = `/admin/campaign/design/page-logic/${campaignId}`;
        } else if (me.type === 'Admin') {
            urlToBack = `/admin/dashboard`;
        }
        navigate(urlToBack);
    };

    return (
        <Box
            sx={{
                ...styles.startSurveyLayout,
                backgroundImage: `url(${surveyImage ?? imageBackground})`,
            }}
        >
            <Grid
                container
                spacing={2}
                sx={{
                    alignItems: ['flex-end'],
                    marginBottom: ['3rem', '4rem', '6rem', '8rem'],
                }}
            >
                <Grid item xs={12} md={4} sm={6}>
                    <BrandLogo />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={8}
                    sx={{
                        display: ['none', 'flex', 'flex', 'flex'],
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    {me.creator && (
                        <IconButton
                            onClick={() =>
                                closeSurvey(Number(surveyState.id), me)
                            }
                            className="close-surveys"
                        >
                            <CloseIcon
                                sx={{
                                    color: (theme) =>
                                        theme.palette.common.white,
                                    width: [
                                        '2.5rem',
                                        '2rem',
                                        '2rem',
                                        '3rem',
                                        '3rem',
                                    ],
                                    height: [
                                        '2.5rem',
                                        '2rem',
                                        '2rem',
                                        '3rem',
                                        '3rem',
                                    ],
                                }}
                            />
                        </IconButton>
                    )}
                </Grid>
            </Grid>

            <Grid
                container
                spacing={2}
                sx={{
                    marginBottom: ['3rem'],
                }}
            >
                <Grid item xs={12} md={7} lg={5} xl={5}>
                    <Typography
                        variant="h4"
                        align="left"
                        sx={{
                            fontSize: [
                                '2rem',
                                '2rem',
                                '3rem',
                                '3.5rem',
                                '3.5rem',
                            ],
                            fontWeight: '900',
                            lineHeight: [
                                '2.5rem',
                                '2.5rem',
                                '3.5rem',
                                '4rem',
                                '4rem',
                            ],
                            color: welcomeTextColor,
                            marginBottom: [
                                '1rem',
                                '1rem',
                                '1rem',
                                '2rem',
                                '2rem',
                            ],
                        }}
                    >
                        {surveyState.title}
                    </Typography>
                    <Typography
                        variant="h5"
                        align="left"
                        sx={{
                            fontSize: [
                                '1.5rem',
                                '1.5rem',
                                '2rem',
                                '2.2rem',
                                '2.2rem',
                            ],
                            fontWeight: '700',
                            lineHeight: [
                                '2rem',
                                '2rem',
                                '2.5rem',
                                '3rem',
                                '3rem',
                            ],
                            color: welcomeTextColor,
                            marginBottom: ['1rem', '1rem', '1.5rem', '1.5rem'],
                        }}
                    >
                        Cảm ơn bạn đã hoàn thành khảo sát
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={5} lg={7} xl={7}>
                    {rewards.length > 0 && (
                        <WheelOfName
                            rewards={rewards}
                            winningReward={winningReward}
                            onSpin={handleSpin}
                            isWinning={isWinning}
                            resultMessage={resultMessage}
                        />
                    )}
                </Grid>
            </Grid>
            {openModalShareSurvey && (
                <ModalShareSurvey
                    open={openModalShareSurvey}
                    onClose={() => setOpenModalShareSurvey(false)}
                />
            )}
            {openModalCampaignStatus && (
                <ModalConfirm
                    open={true}
                    onClose={() => setOpenModalCampaignStatus(false)}
                    title="Thông báo"
                    content={
                        <Box
                            sx={{
                                fontSize: '24px',
                            }}
                        >
                            Chiến dịch khảo sát
                            <b> {`"${surveyState.title}"`}</b> đã kết thúc.
                            <br />
                            Vui lòng kiểm tra lại sau hoặc tham các chiến dịch
                            khác
                        </Box>
                    }
                />
            )}
        </Box>
    );
};
const styles = createStyleSheet({
    startSurveyLayout: {
        width: '100vw',
        minHeight: '100vh',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        padding: ['2rem', '2rem', '4rem', '4rem'],
    },
    btnStartSurvey: {
        padding: [
            '1rem 3rem',
            '1rem 3rem',
            '1.2rem 3.2rem',
            '1.5rem 3.5rem',
            '1.5rem 1.5rem',
        ],
        minWidth: ['10rem', '10rem', '15rem', '15rem'],
        height: ['2.5rem', '2.7rem', '3rem', '4rem', '4rem'],
        borderRadius: '50px',
        fontSize: ['1rem', '1rem', '1.2rem', '1.5rem', '1.5rem'],
        fontWeight: '700',
        lineHeight: ['1.5rem', '1.5rem', '2rem'],
    },
    btnShare: {
        minWidth: ['5rem', '7rem', '10rem', '12rem', '13rem'],
        height: ['2.5rem', '2rem', '2rem', '3rem', '3rem'],
        textTransform: 'none',
        borderRadius: '40px',
        fontSize: ['0.6rem', '0.7rem', '0.7rem', '1rem', '1rem'],
        fontWeight: '700',
        padding: ['1rem', '1rem', '1rem', '1.5rem 3rem'],
    },
});
export default EndSurvey;
